import { action } from '@ember/object';
import { type Registry as Services, service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import {
  PrismBanner,
  PrismButton,
  PrismCard,
  PrismInput,
} from '@mylighthouse/prism-ember';
import { task } from 'ember-concurrency';
import { t } from 'ember-intl';
import { not, or } from 'ember-truth-helpers';
import {
  getErrorTranslationKey,
  LOGIN_CONTEXT,
  shouldReportToSentry,
  VALIDATION_TRANSLATION_KEYS,
} from 'frontend-payments/utils/error-handler';
import isValidEmailAddress from 'frontend-utils/utils/is-valid-email-address';

import styles from './email-verification.module.css';

interface LoginEmailVerificationSignature {
  Args: {
    onEmailVerified: (email: string) => void;
  };
}

export default class LoginEmailVerification extends Component<LoginEmailVerificationSignature> {
  @service declare auth: Services['auth'];
  @service declare errorTracking: Services['error-tracking'];
  @service declare intl: Services['intl'];

  @tracked email = '';
  @tracked error = '';

  submitTask = task(async (): Promise<void> => {
    if (!this.isValidEmail) {
      this.error = this.intl.t(VALIDATION_TRANSLATION_KEYS.INVALID_EMAIL);
      return;
    }

    this.error = '';

    try {
      await this.auth.requestTotpCode(this.email);
      this.args.onEmailVerified(this.email);
    } catch (error) {
      const translationKey = getErrorTranslationKey(
        error,
        LOGIN_CONTEXT.EMAIL_LOGIN,
      );
      this.error = this.intl.t(translationKey);

      // Only report errors that are system issues, not user errors
      if (shouldReportToSentry(error)) {
        // Set Sentry tags for easy filtering
        this.errorTracking.setTag('feature', 'authentication');
        this.errorTracking.setTag('login_step', 'email_verification');

        this.errorTracking.setUser({
          email: this.email,
        });

        this.errorTracking.captureException(error);
      }
    }
  });

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get isValidEmail() {
    return isValidEmailAddress(this.email);
  }

  @action
  async handleSubmit(): Promise<void> {
    await this.submitTask.perform();
  }

  @action
  setEmail(event: Event): void {
    const target = event.target as HTMLInputElement;
    this.email = target.value;
  }

  @action
  async submitFormWithEnter(event: KeyboardEvent): Promise<void> {
    if (event.key === 'Enter' && this.isValidEmail) {
      await this.handleSubmit();
    }
  }

  @action
  updateEmail(event: Event): void {
    const target = event.target as HTMLInputElement;
    this.email = target.value;
    if (this.error) {
      this.error = '';
    }
  }

  <template>
    <div class={{styles.container}}>
      <PrismCard @contentLayout="noPadding" class={{styles.form}}>
        <:content>
          <h2 class="text-emphasis">
            {{t "payments.login.verify_your_account"}}
          </h2>
          {{#if this.error}}
            <PrismBanner
              @bannerMessage={{this.error}}
              @bannerStyle="critical"
              data-test-error-banner
            />
          {{/if}}
          <div>
            <div class="flex w-full">
              <PrismInput
                @autofocus={{true}}
                @errorMessage={{t "payments.login.error.email"}}
                @hasErrors={{not this.isValidEmail}}
                @label={{t "payments.login.email"}}
                @onInput={{this.setEmail}}
                @onKeyDown={{this.submitFormWithEnter}}
                @removePadding={{true}}
                @type="email"
                @value={{this.email}}
                data-test-email-input
              />
            </div>
            <PrismButton
              @isDisabled={{or
                (not this.isValidEmail)
                this.submitTask.isRunning
              }}
              @isLoading={{this.submitTask.isRunning}}
              @label={{t "payments.login"}}
              @onClick={{this.handleSubmit}}
              class="mt-200 w-full"
              data-test-submit-button
            />
          </div>
        </:content>
      </PrismCard>
    </div>
  </template>
}
